<template>
  <!-- Static backdrop + no close button, the LanguageConflictModal pattern:
       this is a one-shot explainer and dismissing it writes a permanent seen
       flag, so a stray backdrop tap, an Esc, or (on mobile) a swipe on the
       sheet body must not burn the single showing before it is read. The CTA
       is the only way out. -->
  <Modal
    :model-value="modelValue"
    :title="$t('freebies.modalTitle')"
    backdrop="static"
    :hide-close-button="true"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="freebies-explainer">
      <div class="freebies-badge" aria-hidden="true">
        <font-awesome-icon icon="gift" />
      </div>
      <p class="freebies-lead">
        {{
          $t("freebies.modalBody", {
            chats: chatsBonus,
            lessons: lessonsBonus,
          })
        }}
      </p>
    </div>
    <template #footer>
      <button
        type="button"
        class="btn btn-primary freebies-cta"
        @click="$emit('update:modelValue', false)"
      >
        {{ $t("freebies.modalCta") }}
      </button>
    </template>
  </Modal>
</template>

<script>
import Modal from "@/components/Modal.vue";

/**
 * One-time explainer for the mobile-launch bonus. Purely presentational: the
 * host (DashboardView) owns whether this account is granted, whether the
 * explainer has already been seen, and whether another modal is in the way.
 *
 * The bonus arrives as two plain numbers rather than the `freebies` node so
 * this component can never become a second place that coerces it. There is
 * exactly one client-side derivation, in `useUsageCounts`, and every consumer
 * reads it there.
 */
export default {
  name: "FreebiesExplainerModal",
  components: { Modal },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    // Already coerced by useUsageCounts.getFreebiesBonus. The grant RAISES the
    // free lifetime ceiling, so these are what it ADDS, never a total.
    chatsBonus: {
      type: Number,
      default: 0,
    },
    lessonsBonus: {
      type: Number,
      default: 0,
    },
  },
  emits: ["update:modelValue"],
};
</script>

<style scoped>
.freebies-explainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--al-space-md);
  text-align: center;
  padding: var(--al-space-sm) 0;
}

/* DESIGN.md assigns the warm accent (#EB882D, the --al-orange token) to
   achievement and premium emphasis and explicitly not to primary actions, so
   it carries the badge while the CTA stays on the standard primary button.
   --al-warm-soft is a solid warm tint in light mode and a low-alpha wash in
   dark, so the badge reads the same in both themes.
   The glyph is aria-hidden and purely decorative (every word of the message
   is text on --al-text), but the accent on the soft warm tint is a low-
   contrast pair in light mode, so the ring gives the shape a defined edge
   instead of letting it wash out. */
.freebies-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: var(--al-radius-pill);
  border: 1px solid var(--al-orange);
  background-color: var(--al-warm-soft);
  color: var(--al-orange);
  font-size: 1.75rem;
  flex-shrink: 0;
}

/* Body copy stays on --al-text: orange on either surface misses AA at this
   size. */
.freebies-lead {
  color: var(--al-text);
  line-height: 1.5;
  margin: 0;
}

.freebies-cta {
  min-width: 8rem;
}
</style>
